/**
 * Inventory Forecasting Tool - Utility Functions
 *
 * Pure utility functions used across the Inventory page components.
 * These functions have no side effects and can be safely reused.
 */

import moment from "moment";
import { SalesModifiersData, ProjectionBreakdown } from "./types";
import { InventoryGrouping } from "models/InventoryModel";

// ============================================================================
// Array Utilities
// ============================================================================

/**
 * Returns an array of unique strings from the input.
 * Preserves the first occurrence of each value.
 */
export function getUniqueStrings(values: string[]): string[] {
  return Array.from(new Set(values));
}

/**
 * Splits an array into chunks of the specified size.
 * Useful for batching API calls.
 *
 * @example
 * chunkArray([1,2,3,4,5], 2) // [[1,2], [3,4], [5]]
 */
export function chunkArray<T>(values: T[], chunkSize: number): T[][] {
  const chunks: T[][] = [];
  for (let index = 0; index < values.length; index += chunkSize) {
    chunks.push(values.slice(index, index + chunkSize));
  }
  return chunks;
}

// ============================================================================
// Number Formatting
// ============================================================================

/**
 * Formats a number for display in the inventory table.
 * Returns empty string for null/undefined/non-finite values.
 * Uses locale-aware formatting with commas.
 */
export function formatNumber(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return "";
  return value.toLocaleString();
}

// ============================================================================
// Sales Modifiers Utilities
// ============================================================================

/**
 * Get the effective modifier for a given inventory and month.
 * Per-inventory modifier takes priority over global modifier.
 * Returns undefined if no modifier is set (meaning no adjustment).
 *
 * Priority order:
 * 1. Inventory-specific modifier for the month
 * 2. Global modifier for the month
 * 3. undefined (no modifier applied)
 *
 * @param modifiers - The complete sales modifiers data
 * @param inventoryKey - The inventory_id to look up
 * @param monthKey - The month in "YYYY-MM" format
 */
export function getEffectiveModifier(
  modifiers: SalesModifiersData,
  inventoryKey: string,
  monthKey: string,
): number | undefined {
  const inventoryModifier = modifiers.byInventory[inventoryKey]?.[monthKey];
  if (inventoryModifier !== undefined) {
    return inventoryModifier;
  }
  return modifiers.global[monthKey];
}

// ============================================================================
// Projection Breakdown Utilities
// ============================================================================

/**
 * Calculate projection breakdown for display in the detail dialog.
 * Shows how the run rate was calculated based on the configured basis mode.
 *
 * @param inventoryGrouping - The inventory grouping with run rate data
 * @param runRateBasisMode - "rolling" or "fixed" mode
 * @param runRateBasis - Number of days for rolling mode
 * @param fixedRunRateStart - Start date for fixed mode (YYYY-MM-DD)
 * @param fixedRunRateEnd - End date for fixed mode (YYYY-MM-DD)
 */
export function calculateProjectionBreakdown(
  inventoryGrouping: InventoryGrouping,
  runRateBasisMode: "rolling" | "fixed",
  runRateBasis: number,
  fixedRunRateStart: string | null,
  fixedRunRateEnd: string | null,
): ProjectionBreakdown {
  const dailySeries = inventoryGrouping?.runRate?.dailySeries || [];
  const today = moment();

  let dateRangeStart: string;
  let dateRangeEnd: string;

  if (runRateBasisMode === "fixed" && fixedRunRateStart && fixedRunRateEnd) {
    dateRangeStart = fixedRunRateStart;
    dateRangeEnd = fixedRunRateEnd;
  } else {
    // Rolling mode: last N completed days, excluding today. Matches the server,
    // which builds dailySeries up to yesterday in runRate.js.
    dateRangeEnd = today.clone().subtract(1, "days").format("YYYY-MM-DD");
    dateRangeStart = today.clone().subtract(runRateBasis, "days").format("YYYY-MM-DD");
  }

  // Sum sales within the date range
  let totalSales = 0;
  let daysWithData = 0;

  for (const point of dailySeries) {
    const pointDate = point?.date;
    if (!pointDate) continue;

    if (pointDate >= dateRangeStart && pointDate <= dateRangeEnd) {
      totalSales += Number(point?.sold || 0);
      daysWithData++;
    }
  }

  // Calculate the actual number of days in the range
  const startMoment = moment(dateRangeStart, "YYYY-MM-DD");
  const endMoment = moment(dateRangeEnd, "YYYY-MM-DD");
  const daysInRange = endMoment.diff(startMoment, "days") + 1;

  // Calculate daily average (use days with data if available, otherwise full range)
  const dailyAverage = daysWithData > 0 ? totalSales / daysWithData : 0;

  // Build human-readable explanation
  const formattedStart = startMoment.format("MMM D, YYYY");
  const formattedEnd = endMoment.format("MMM D, YYYY");
  const formattedAverage = dailyAverage.toFixed(1);

  let explanation: string;
  if (runRateBasisMode === "fixed") {
    explanation = `Using fixed date range: ${formattedStart} - ${formattedEnd} had ${Math.round(totalSales).toLocaleString()} sales → ${formattedAverage}/day average`;
  } else {
    explanation = `Using rolling ${runRateBasis}-day window: ${formattedStart} - ${formattedEnd} had ${Math.round(totalSales).toLocaleString()} sales → ${formattedAverage}/day average`;
  }

  return {
    dateRangeStart,
    dateRangeEnd,
    totalSales,
    daysInRange,
    dailyAverage,
    explanation,
  };
}
